/**
 * 📊 STRUCTURED LOGGER
 *
 * Logger estruturado para operações críticas do sistema.
 * Garante rastreabilidade de erros com contexto multi-tenant completo.
 *
 * Em produção, substitua o console.error por envio a um serviço como
 * Sentry, Datadog, ou tabela de audit_logs no Supabase.
 */

export interface StructuredLogContext {
  operation: string;
  organizationId?: string;
  blueprintId?: string;
  versionId?: string;
  instanceId?: string;
  taskId?: string;
  userId?: string;
  extra?: Record<string, unknown>;
}

export interface StructuredLogEntry extends StructuredLogContext {
  level: 'info' | 'warn' | 'error';
  message: string;
  error?: unknown;
  timestamp: string;
}

function buildEntry(
  level: StructuredLogEntry['level'],
  message: string,
  context: StructuredLogContext,
  error?: unknown,
): StructuredLogEntry {
  return {
    level,
    message,
    ...context,
    error,
    timestamp: new Date().toISOString(),
  };
}

function serialize(entry: StructuredLogEntry): string {
  const err =
    entry.error instanceof Error
      ? { message: entry.error.message, stack: entry.error.stack, code: (entry.error as any).code }
      : entry.error;

  return JSON.stringify({ ...entry, error: err }, null, 0);
}

export const structuredLogger = {
  info(message: string, context: StructuredLogContext): void {
    const entry = buildEntry('info', message, context);
    if (import.meta.env.DEV) {
      console.info(`[${entry.operation}]`, message, context);
    }
  },

  warn(message: string, context: StructuredLogContext, error?: unknown): void {
    const entry = buildEntry('warn', message, context, error);
    console.warn(`[${entry.operation}] WARN:`, message, serialize(entry));
  },

  error(message: string, context: StructuredLogContext, error?: unknown): void {
    const entry = buildEntry('error', message, context, error);
    console.error(`[${entry.operation}] ERROR:`, message, serialize(entry));
    // TODO: send to error tracking service (Sentry, Datadog, etc.)
    // errorTracker.captureException(error, { extra: entry });
  },
};
